// "Minhas tarefas" da Visão Geral — #6493.
//
// Separa o que está aberto no nome da pessoa (atividades do CRM e solicitações
// da Central) pelo vencimento: atrasadas, hoje, próximos 7 dias, depois e sem
// data. Lógica pura.

export interface TarefaAberta {
  id: number | string;
  titulo: string;
  vencimento: string | null;
  status: string | null;
  categoria: string | null;
  solicitacao_status?: string | null;
  prioridade?: string | null;
  /** Atividade do CRM ligada a uma negociação: abre a negociação. */
  negociacao_id?: number | null;
}

export interface MinhasTarefas {
  atrasadas: TarefaAberta[];
  hoje: TarefaAberta[];
  proximas: TarefaAberta[];
  depois: TarefaAberta[];
  semData: TarefaAberta[];
  /** Solicitações da Central abertas com a pessoa, em qualquer grupo acima. */
  solicitacoes: number;
}

const STATUS_FECHADO = new Set(['concluido', 'cancelado']);
const SOLICITACAO_FECHADA = new Set(['concluido', 'rejeitado']);

/** Ainda é trabalho a fazer? Solicitação rejeitada pode ter status antigo "a iniciar". */
export function estaAberta(t: TarefaAberta): boolean {
  if (STATUS_FECHADO.has(String(t.status || ''))) return false;
  if (t.categoria === 'solicitacao' && SOLICITACAO_FECHADA.has(String(t.solicitacao_status || ''))) return false;
  return true;
}

const somarDias = (iso: string, dias: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  const dt = new Date(a, m - 1, d + dias);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Agrupa pelo vencimento. `hojeIso` = 'AAAA-MM-DD' local. Dentro de cada grupo, o mais antigo primeiro. */
export function classificarMinhasTarefas(lista: TarefaAberta[], hojeIso: string): MinhasTarefas {
  const limite = somarDias(hojeIso, 7);
  const r: MinhasTarefas = { atrasadas: [], hoje: [], proximas: [], depois: [], semData: [], solicitacoes: 0 };
  for (const t of lista) {
    if (!estaAberta(t)) continue;
    if (t.categoria === 'solicitacao') r.solicitacoes++;
    const venc = t.vencimento ? t.vencimento.slice(0, 10) : null;
    if (!venc) r.semData.push(t);
    else if (venc < hojeIso) r.atrasadas.push(t);
    else if (venc === hojeIso) r.hoje.push(t);
    else if (venc <= limite) r.proximas.push(t);
    else r.depois.push(t);
  }
  const porVencimento = (a: TarefaAberta, b: TarefaAberta) => String(a.vencimento || '').localeCompare(String(b.vencimento || ''));
  r.atrasadas.sort(porVencimento);
  r.proximas.sort(porVencimento);
  r.depois.sort(porVencimento);
  return r;
}

/** A fila que a tela mostra: atrasadas, hoje e os próximos 7 dias, até `max`. */
export function filaDoDia(m: MinhasTarefas, max = 8): TarefaAberta[] {
  return [...m.atrasadas, ...m.hoje, ...m.proximas].slice(0, max);
}

/**
 * Só as solicitações da Central (sem as atividades do CRM). Telas de quem trabalha pela
 * Central (Tecnologia, Marketing): visita ou ligação esquecida no CRM não é demanda.
 */
export function soSolicitacoes(m: MinhasTarefas): MinhasTarefas {
  const so = (l: TarefaAberta[]) => l.filter(t => t.categoria === 'solicitacao');
  return { atrasadas: so(m.atrasadas), hoje: so(m.hoje), proximas: so(m.proximas), depois: so(m.depois), semData: so(m.semData), solicitacoes: m.solicitacoes };
}
